import { useState } from 'react'
import type { ReactNode } from 'react'
import { useKitchensink } from '../context'
import { theme } from '../theme'

const inputStyle = {
  backgroundColor: theme.surfaceElevated,
  border: `1px solid ${theme.border}`,
  borderRadius: '7px',
  color: theme.text,
  fontSize: '13px',
  padding: '8px 12px',
  outline: 'none',
  width: '280px',
  fontFamily: 'inherit',
} as const

const labelStyle = {
  fontSize: '11px',
  color: theme.textMuted,
  marginBottom: '6px',
  display: 'block',
} as const

const buttonStyle = {
  alignSelf: 'flex-start' as const,
  backgroundColor: theme.accent,
  color: '#fff',
  border: 'none',
  borderRadius: '7px',
  padding: '8px 18px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
} as const

const codeStyle = {
  fontFamily: 'monospace',
  fontSize: '12px',
  color: theme.accent,
  backgroundColor: theme.accentSubtle,
  borderRadius: '4px',
  padding: '1px 5px',
} as const

type StepProps = { n: number; children: ReactNode }

const Step = ({ n, children }: StepProps) => (
  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
    <span
      style={{
        flexShrink: 0,
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        backgroundColor: theme.accentSubtle,
        color: theme.accent,
        fontSize: '11px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '1px',
      }}
    >
      {n}
    </span>
    <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0, lineHeight: 1.6 }}>
      {children}
    </p>
  </div>
)

export const EmailPage = () => {
  const { variant, isInitialized } = useKitchensink()
  const [secondVisible, setSecondVisible] = useState(false)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '36px' }}>
      <div>
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: theme.text,
            margin: '0 0 6px',
            letterSpacing: '-0.02em',
          }}
        >
          ✉️ Email Tracking Page
        </h1>
        <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>
          Route: <code style={codeStyle}>{variant === 'spa' ? '/email' : '/email/'}</code>
        </p>
      </div>

      {/* Steps */}
      <div
        style={{
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: '10px',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <p
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            margin: '0 0 4px',
          }}
        >
          How to test
        </p>
        <Step n={1}>
          Fill in the tenant credentials above and click{' '}
          <strong style={{ color: theme.text }}>Initialize</strong> to start the snippet.
        </Step>
        <Step n={2}>
          Navigate here from another page (e.g. Home → Email Tracking) to verify the tracker{' '}
          {variant === 'spa'
            ? 're-scans on SPA navigation and picks up '
            : 'scans the fresh page load and picks up '}
          <strong style={{ color: theme.text }}>Form 1</strong>.
        </Step>
        <Step n={3}>
          Type a valid email in <strong style={{ color: theme.text }}>Form 1</strong>, e.g.{' '}
          <code style={codeStyle}>john@acme.com</code>, then click{' '}
          <strong style={{ color: theme.text }}>Submit</strong>.
        </Step>
        <Step n={4}>
          Open the console and confirm a <code style={codeStyle}>[jitsu] identify</code> log and a{' '}
          <code style={codeStyle}>[jitsu] track FormSubmit</code> log both appear with{' '}
          <code style={codeStyle}>formId: &quot;form-1&quot;</code> and the correct email.
        </Step>
        <Step n={5}>
          Click <strong style={{ color: theme.text }}>Reveal Form 2</strong> below to mount the
          second form dynamically and verify the MutationObserver picks it up.
        </Step>
        <Step n={6}>
          Type a valid email in <strong style={{ color: theme.text }}>Form 2</strong>, e.g.{' '}
          <code style={codeStyle}>jane@acme.com</code>, then click{' '}
          <strong style={{ color: theme.text }}>Submit</strong>.
        </Step>
        <Step n={7}>
          Confirm a second <code style={codeStyle}>[jitsu] identify</code> and{' '}
          <code style={codeStyle}>[jitsu] track FormSubmit</code> appear with{' '}
          <code style={codeStyle}>formId: &quot;form-2&quot;</code> and the correct email.
        </Step>
      </div>

      {/* Form 1 */}
      {isInitialized && (
        <div
          style={{
            backgroundColor: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: '10px',
            padding: '20px 24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: theme.text }}>Form 1</span>
            <span style={{ fontSize: '12px', color: theme.textMuted }}>— present on page load</span>
          </div>
          <div
            style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' as const, marginBottom: '16px' }}
          >
            <code style={codeStyle}>type=&quot;text&quot;</code>
            <code style={codeStyle}>name=&quot;contact-email&quot;</code>
          </div>
          <form
            id='form-1'
            name='form-1'
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            onSubmit={e => e.preventDefault()}
          >
            <label htmlFor='email-field-1' style={labelStyle}>
              Contact email
            </label>
            <input
              id='email-field-1'
              name='contact-email'
              type='text'
              placeholder='e.g. john@acme.com'
              style={inputStyle}
            />
            <button type='submit' style={{ ...buttonStyle, marginTop: '8px' }}>
              Submit
            </button>
          </form>
        </div>
      )}

      {/* Reveal button */}
      {isInitialized && !secondVisible && (
        <button
          data-test-id='reveal-form-2'
          onClick={() => setSecondVisible(true)}
          style={{
            ...buttonStyle,
            backgroundColor: theme.surfaceElevated,
            color: theme.text,
            border: `1px solid ${theme.border}`,
          }}
        >
          Reveal Form 2
        </button>
      )}

      {/* Form 2 */}
      {isInitialized && secondVisible && (
        <div
          style={{
            backgroundColor: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: '10px',
            padding: '20px 24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: theme.text }}>Form 2</span>
            <span style={{ fontSize: '12px', color: theme.textMuted }}>
              — mounted on button click
            </span>
          </div>
          <div
            style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' as const, marginBottom: '16px' }}
          >
            <code style={codeStyle}>type=&quot;email&quot;</code>
            <code style={codeStyle}>name=&quot;email&quot;</code>
          </div>
          <form
            id='form-2'
            name='form-2'
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            onSubmit={e => e.preventDefault()}
          >
            <label htmlFor='email-field-2' style={labelStyle}>
              Email
            </label>
            <input
              id='email-field-2'
              name='email'
              type='email'
              placeholder='e.g. jane@acme.com'
              style={inputStyle}
            />
            <button type='submit' style={{ ...buttonStyle, marginTop: '8px' }}>
              Submit
            </button>
          </form>
        </div>
      )}
    </div>
  )
}
